import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ChevronRight, Lock, Zap, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { api, pollRefresh } from '../lib/api';

const SEVERITY = {
  red:    { bg: 'bg-[#EF4444]', border: 'border-[#EF4444]', text: 'text-[#EF4444]', pill: 'bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444]' },
  orange: { bg: 'bg-[#F59E0B]', border: 'border-[#F59E0B]', text: 'text-[#F59E0B]', pill: 'bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#F59E0B]' },
  yellow: { bg: 'bg-[#FBBF24]', border: 'border-[#FBBF24]', text: 'text-[#FBBF24]', pill: 'bg-[#FBBF24]/10 border-[#FBBF24]/30 text-[#FBBF24]' },
  green:  { bg: 'bg-[#10B981]', border: 'border-[#10B981]', text: 'text-[#10B981]', pill: 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]' },
};

const SEVERITY_HEX = { red: '#EF4444', orange: '#F59E0B', yellow: '#FBBF24', green: '#10B981' };

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const PLACEHOLDER_ACTIONS = [
  { id: 'ph1', title: 'Review overdue invoices from Q3', severity: 'red', why: 'Three invoices are overdue by more than 30 days totaling significant revenue.', impact_estimate: '₹2.4L at risk' },
  { id: 'ph2', title: 'Renegotiate supplier pricing for bulk orders', severity: 'orange', why: 'Current costs are 12% above market average for your top 5 SKUs.', impact_estimate: '₹85K savings' },
  { id: 'ph3', title: 'Expand to adjacent market segments', severity: 'green', why: 'Your customer data shows strong demand from an untapped demographic.', impact_estimate: '₹1.8L opportunity' },
];

export default function TodayEngine() {
  const { token, hasPlan } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isPremium = hasPlan('standard');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [forceFree, setForceFree] = useState(false);

  // Load on mount for premium users
  useEffect(() => {
    if (!token || !isPremium) return;
    setLoading(true);
    api.aiToday(token)
      .then(r => setData(r))
      .catch(e => {
        if (e?.status === 403) { setForceFree(true); return; }
        if (e?.status === 429 || (e?.status >= 500)) {
          toast.show('Could not load insights — using cached data', 'error');
        }
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isPremium]);

  const doRefresh = async () => {
    if (!token || refreshing) return;
    setRefreshing(true);
    const prevGenAt = data?.generated_at || null;
    try {
      const r = await pollRefresh({
        postFn: () => api.aiTodayRefresh(token),
        getFn: () => api.aiToday(token),
        prevGeneratedAt: prevGenAt,
        pollIntervalMs: 5000,
        maxWaitMs: 120000,
      });
      setData(r);
      toast.show('Today Engine refreshed', 'success');
    } catch (e) {
      if (e?.status === 429) toast.show('Rate limited — try again in a minute', 'error');
      else toast.show(e?.message || 'Refresh failed', 'error');
      // keep old data
    } finally {
      setRefreshing(false);
    }
  };

  const handleCardClick = (action) => {
    if (!action.route) return;
    const params = action.route_params ? '?' + new URLSearchParams(action.route_params).toString() : '';
    navigate(action.route + params);
  };

  // Free / Basic users — blocked state (also triggered by 403 fallback)
  if (!isPremium || forceFree) {
    return (
      <div className="glass-card rounded-3xl overflow-hidden" data-testid="today-engine-locked">
        <div className="p-5 pb-3">
          <h2 className="text-xl font-extrabold text-white tracking-tight" data-testid="today-engine-title">
            What should I do today?
          </h2>
        </div>
        <div className="relative px-5 pb-5">
          {/* Placeholder cards — blurred via CSS filter */}
          <div className="space-y-3 select-none pointer-events-none" style={{ filter: 'blur(8px)' }} aria-hidden="true">
            {PLACEHOLDER_ACTIONS.map(a => (
              <PlaceholderCard key={a.id} action={a} />
            ))}
          </div>
          {/* Sharp CTA overlay with dark tint */}
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-b-3xl bg-black/30">
            <Lock size={28} className="text-white/40 mb-3" />
            <p className="text-white/70 font-semibold text-sm mb-4">Premium AI strategy insights</p>
            <button
              data-testid="today-unlock-cta"
              onClick={() => navigate('/billing')}
              className="px-6 py-3 rounded-2xl bg-gradient-to-r from-[#7C5CFF] to-teal-500 text-white font-bold text-sm shadow-lg shadow-[#7C5CFF]/25 hover:shadow-xl hover:shadow-[#7C5CFF]/35 transition-all hover:scale-[1.02]"
            >
              <span className="flex items-center gap-2"><Zap size={16} /> Unlock AI Strategy</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const actions = data?.actions || [];

  return (
    <div className="glass-card rounded-3xl overflow-hidden" data-testid="today-engine">
      {/* Header */}
      <div className="flex items-start justify-between p-5 pb-3">
        <div>
          <h2 className="text-xl font-extrabold text-white tracking-tight" data-testid="today-engine-title">
            What should I do today?
          </h2>
          {data && (
            <p className="text-white/40 text-xs mt-1.5 flex items-center gap-2 flex-wrap" data-testid="today-engine-meta">
              <span>Updated {timeAgo(data.generated_at)}</span>
              <span className="w-1 h-1 rounded-full bg-white/20" />
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase ${
                data.source === 'llm' ? 'bg-teal-500/15 text-teal-400' : 'bg-white/8 text-white/50'
              }`}>
                {data.source || 'cache'}
              </span>
              {data.degraded && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-amber-500/15 text-amber-400">
                  using fallback insights
                </span>
              )}
            </p>
          )}
        </div>
        <button
          data-testid="today-refresh-btn"
          onClick={doRefresh}
          disabled={refreshing || loading}
          className="p-2.5 rounded-xl bg-white/[0.06] border border-white/10 text-white/60 hover:text-white hover:bg-white/10 transition-all disabled:opacity-40"
          title="Refresh insights"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Actions list */}
      <div className="px-5 pb-5">
        {loading && !data ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-20 rounded-2xl bg-white/[0.04] animate-pulse" />)}
          </div>
        ) : actions.length === 0 ? (
          <div className="flex items-center gap-3 p-5 rounded-2xl bg-white/[0.04] border border-white/[0.06]" data-testid="today-empty">
            <CheckCircle2 size={24} className="text-emerald-400 flex-shrink-0" />
            <p className="text-white/60 text-sm">All clear for today — check back later.</p>
          </div>
        ) : (
          <div className="space-y-2.5 max-h-[440px] overflow-y-auto pr-1 custom-scrollbar" data-testid="today-actions-list">
            {actions.map((a, i) => (
              <ActionCard key={a.id || i} action={a} onClick={() => handleCardClick(a)} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionCard({ action, onClick, index }) {
  const s = SEVERITY[action.severity] || SEVERITY.green;
  const hex = SEVERITY_HEX[action.severity] || '#10B981';

  return (
    <motion.button
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
      onClick={onClick}
      data-testid={`today-action-${action.id}`}
      className="w-full text-left flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07] hover:border-white/[0.10] transition-all group cursor-pointer"
    >
      {/* Color rail */}
      <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${s.bg}`} style={{ minHeight: 40 }} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm truncate">{action.title}</p>
        <p className="text-white/45 text-xs mt-1 line-clamp-2 leading-relaxed">{action.why}</p>
      </div>

      {/* Impact pill */}
      {action.impact_estimate && (
        <span
          className="flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold tabular-nums border"
          style={{
            color: hex,
            borderColor: `${hex}40`,
            backgroundColor: `${hex}15`,
          }}
          data-testid={`today-impact-${action.id}`}
        >
          {action.impact_estimate}
        </span>
      )}

      {/* Chevron */}
      <ChevronRight size={16} className="text-white/25 group-hover:text-white/50 transition-colors flex-shrink-0" />
    </motion.button>
  );
}

function PlaceholderCard({ action }) {
  const s = SEVERITY[action.severity] || SEVERITY.green;
  return (
    <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.04] border border-white/[0.06]">
      <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${s.bg}`} style={{ minHeight: 40 }} />
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm truncate">{action.title}</p>
        <p className="text-white/45 text-xs mt-1 line-clamp-2 leading-relaxed">{action.why}</p>
      </div>
      <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold tabular-nums border border-white/15 text-white/50 bg-white/5">
        {action.impact_estimate}
      </span>
      <ChevronRight size={16} className="text-white/25 flex-shrink-0" />
    </div>
  );
}
